import { useContext, useState, useRef } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const [phone, setPhone] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState(null);
  const [step, setStep] = useState("input"); // input | otp
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const resendTimer = useRef(null);

  const sendOtp = () => {
    setError("");
    const digits = phone.replace(/[^0-9]/g, "");
    if (!digits || digits.length < 10) {
      setError("Please enter a valid phone number.");
      return;
    }

    // Generate a 6-digit test OTP (in production you'd call your backend / SMS gateway)
    const code = String(Math.floor(100000 + Math.random() * 900000));
    setGeneratedOtp(code);
    setStep("otp");
    setMessage(`Test OTP sent: ${code}`); // visible for test/dev

    // simple resend cooldown (30s) - not shown as countdown UI, just disables rapid re-send
    if (resendTimer.current) clearTimeout(resendTimer.current);
    resendTimer.current = setTimeout(() => setMessage(""), 30000);
  };

  const verifyOtp = () => {
    setError("");
    if (!generatedOtp) {
      setError("No OTP was generated. Please request a code.");
      return;
    }
    if (otpInput.trim() === generatedOtp) {
      // Successful verification
      login({ phone });
      navigate("/checkout");
    } else {
      setError("Incorrect OTP. Please try again.");
    }
  };

  const resend = () => {
    // allow resend: generate a new code
    sendOtp();
  };

  const continueAsGuest = () => {
    login({ guest: true });
    navigate("/checkout");
  };

  return (
    <main className="max-w-[720px] mx-auto px-6 py-16">
      <div className="p-6 bg-white dark:bg-[#2d2a1b] rounded-2xl border border-[#e7e3cf] dark:border-[#3d3a2a] shadow-sm">
        <h1 className="text-2xl font-extrabold mb-4">Sign in / Checkout</h1>

        <p className="text-sm text-[#9a8d4c] mb-6">Use OTP to sign in or continue as guest to checkout without an account.</p>

        {step === "input" && (
          <div className="space-y-4">
            <label className="block text-sm font-medium">Phone number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. +91 98765 43210"
              className="w-full px-4 py-3 rounded-xl border border-[#e7e3cf] bg-transparent"
            />

            <button onClick={sendOtp} className="w-full bg-primary text-background-dark py-3 rounded-xl font-black text-lg shadow-lg hover:brightness-105 transition-all">
              Send OTP
            </button>

            <div className="flex items-center justify-center gap-3">
              <button onClick={continueAsGuest} className="text-sm text-primary underline">Continue as guest</button>
            </div>

            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          </div>
        )}

        {step === "otp" && (
          <div className="space-y-4">
            <p className="text-sm text-[#9a8d4c]">Enter the 6-digit code sent to <strong>{phone}</strong>.</p>
            <input
              type="text"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
              placeholder="123456"
              className="w-full px-4 py-3 rounded-xl border border-[#e7e3cf] bg-transparent text-center text-lg tracking-widest"
            />

            <div className="flex gap-3">
              <button onClick={verifyOtp} className="flex-1 bg-primary text-white py-3 rounded-xl font-bold">Verify & Continue</button>
              <button onClick={() => setStep("input")} className="flex-1 bg-[#f3f1e7] py-3 rounded-xl font-bold">Change Number</button>
            </div>

            <div className="flex items-center justify-between text-sm text-[#9a8d4c]">
              <button onClick={resend} className="text-primary underline">Resend code</button>
              <button onClick={continueAsGuest} className="text-primary underline">Continue as guest</button>
            </div>

            {message && <p className="text-sm text-[#6b6b6b] mt-2">{message}</p>}
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          </div>
        )}
      </div>
    </main>
  );
}